import { downloadCSV } from "ra-core";
import jsonExport from "jsonexport/dist";
import {
  calculateMbf,
  calculateMsf,
  getUnitName,
  isLumberCommodity,
  isPanelsCommodity,
} from "../../../utils/loadTypeUtils";
import { getFormattedStartAndEndDate } from "../../../utils/dateUtils";
import { trimDecimal } from "../../../utils/utils";
import { buildHeader } from "../../../utils/exportsUtils";
import {
  FilePaths,
  InventoryStatus,
  Orientations,
  Unit,
} from "../../../utils/constants";
import { logError } from "../../../utils/appUtils";
import {
  generateAndDownloadPdf,
  generateFileName,
} from "../../../utils/fileUtils";

export function productExportPdf(products, _start, _end, notify, filter) {
  return (async () => {
    try {
      const reloadYardId = window.appUser.reloadYardId;

      // 1) Extract all STORED delivery dates
      const allDates = (products || []).flatMap((p) =>
        (p.inventories?.items || [])
          .filter((inv) => inv.status === InventoryStatus.STORED)
          .map((inv) => inv.receivedAt),
      );
      const { formattedStartDate, formattedEndDate } =
        getFormattedStartAndEndDate(
          allDates,
          new Date(),
          new Date(),
          "receivedAt",
        );

      const rows = [];
      const totalsMap = {};
      let totalPieces = 0,
        totalMBF = 0,
        totalMSF = 0;
      let showPPU = false,
        showFootage = false;

      (products || []).forEach((product) => {
        const {
          name: productName,
          productCode,
          reloadYardCommodityId,
          pcs: prodPcs,
          defaultUOM,
        } = product;

        // Group STORED inventories by (pcs, inventoryUOM)
        const groupMap = {};
        (product.inventories?.items || [])
          .filter((inv) => inv.status === InventoryStatus.STORED)
          .forEach((inv) => {
            const pcsVal = inv?.pcs != null ? inv.pcs : prodPcs;
            const unitsVal = inv?.inventoryUOM || defaultUOM || "";
            const pcsKey = pcsVal != null ? String(pcsVal) : "";
            const key = `${pcsKey}||${unitsVal}`;

            const quantityOnHand = Number(inv.quantityOnHand || 0);
            const quantityCommitted = Number(inv.quantityCommitted || 0);

            if (!groupMap[key]) {
              groupMap[key] = {
                pcs: pcsKey,
                units: unitsVal,
                onHand: 0,
                committed: 0,
                footage: 0,
              };
            }

            groupMap[key].onHand += quantityOnHand;
            groupMap[key].committed += quantityCommitted;

            // compute group footage contribution for this inventory line
            if (isLumberCommodity(reloadYardCommodityId)) {
              const invForCalc = {
                ...inv,
                pcs: pcsVal,
                product: product,
              };
              const mbf = calculateMbf(invForCalc) || 0;
              groupMap[key].footage += mbf * quantityOnHand;
            } else if (isPanelsCommodity(reloadYardCommodityId)) {
              const invForCalc = {
                ...inv,
                pcs: pcsVal,
                product: product,
              };
              const msf = calculateMsf(invForCalc) || 0;
              groupMap[key].footage += msf * quantityOnHand;
            }

            if (pcsVal != null) totalPieces += Number(pcsVal) * quantityOnHand;
          });

        const entries = Object.values(groupMap);
        if (entries.length === 0) return;

        // Emit one row per (pcs, units) group and update totals
        entries.forEach((grp) => {
          showPPU = showPPU || !!grp.pcs;
          showFootage = showFootage || grp.footage > 0;

          totalsMap[grp.units] = (totalsMap[grp.units] || 0) + grp.onHand;
          if (isLumberCommodity(reloadYardCommodityId)) totalMBF += grp.footage;
          else if (isPanelsCommodity(reloadYardCommodityId))
            totalMSF += grp.footage;

          rows.push({
            productName,
            productCode,
            ppu: grp.pcs ? trimDecimal(grp.pcs).toLocaleString() : "--",
            onHand: `${grp.onHand.toLocaleString()} ${getUnitName(grp.units)}`,
            committed: grp.committed.toLocaleString(),
            footage:
              grp.footage > 0
                ? `${trimDecimal(grp.footage).toLocaleString()} ${
                    isLumberCommodity(reloadYardCommodityId)
                      ? Unit.MBF
                      : Unit.MSF
                  }`
                : "--",
          });
        });
      });

      // Build column definitions
      const columns = [
        { label: "Product Name", source: "productName" },
        { label: "Product Code", source: "productCode" },
      ];
      if (showPPU) columns.push({ label: "PPU", source: "ppu" });
      columns.push({ label: "Stocked", source: "onHand" });
      columns.push({ label: "Committed", source: "committed" });
      if (showFootage) columns.push({ label: "Footage", source: "footage" });

      const header = buildHeader(
        products,
        formattedStartDate,
        formattedEndDate,
        filter,
      );
      const footer = [{ label: "Totals", value: "" }];
      Object.entries(totalsMap).forEach(([uom, val]) => {
        const trimmed = trimDecimal(val);
        footer.push({
          label: uom,
          value: trimmed != null ? trimmed.toLocaleString() : "--",
        });
      });
      if (totalPieces)
        footer.push({ label: "Pieces", value: totalPieces.toLocaleString() });
      if (totalMBF)
        footer.push({
          label: "MBF",
          value: trimDecimal(totalMBF).toLocaleString(),
        });
      if (totalMSF)
        footer.push({
          label: "MSF",
          value: trimDecimal(totalMSF, 3).toLocaleString(),
        });

      const fileName = generateFileName({ resource: "products", ext: "pdf" });
      const downloadName = generateFileName({
        resource: "products",
        ext: "pdf",
        includeDate: true,
      });
      await generateAndDownloadPdf({
        templateKey: `${FilePaths.TEMPLATES_PDF}/${FilePaths.TEMPLATE_REPORT}`,
        outputFileKey: `${reloadYardId}/${reloadYardId}/${FilePaths.INVENTORY}/${fileName}`,
        downloadName,
        data: {
          metaData: {
            orientation: Orientations.PORTRAIT,
            currentDate: new Date().toLocaleString(),
          },
          content: {
            header,
            title: "INVENTORY REPORT",
            table: { columns, items: rows },
            footer,
          },
        },
      });
    } catch (err) {
      logError("products:exportPdf", err, notify);
    }
  })();
}

// --- CSV exporter for Products ---
export function productExportCsv(products) {
  try {
    const customHeaderMap = new Map();
    const flat = [];

    (products || []).forEach((product) => {
      const {
        name: productName,
        productCode,
        pcs: prodPcs,
        defaultUOM,
      } = product;

      // const productTotal = Number(product?.totalQuantityOnHand || 0);
      // let printedProductTotal = false;

      // Group STORED inventories by (pcs, inventoryUOM)
      // pcs = inv.pcs if present, else product.pcs
      // units = inv.inventoryUOM if present, else product.defaultUOM
      const groupMap = {};
      (product.inventories?.items || [])
        .filter((inv) => inv.status === InventoryStatus.STORED)
        .forEach((inv) => {
          const pcsVal = inv?.pcs != null ? inv.pcs : prodPcs;
          const unitsVal = inv?.inventoryUOM || defaultUOM || "";
          const pcsKey = pcsVal != null ? String(pcsVal) : "";
          const key = `${pcsKey}||${unitsVal}`;
          const onHand = Number(inv.quantityOnHand || 0);
          const committed = Number(inv.quantityCommitted || 0);
          if (!groupMap[key]) groupMap[key] = { onHand: 0, committed: 0 };
          groupMap[key].onHand += onHand;
          groupMap[key].committed += committed;
        });

      // Emit one CSV row per (pcs, units) group for this product
      Object.entries(groupMap).forEach(([comboKey, agg]) => {
        const [pcsKey, unitsKey] = comboKey.split("||");
        const line = {
          productCode,
          productName,
          pcs: pcsKey,
          stocked: agg.onHand,
          committed: agg.committed,
          inventoryUOM: getUnitName(unitsKey),
          // totalQuantityOnHand: printedProductTotal ? "" : productTotal,
        };
        flat.push(line);
        // printedProductTotal = true;
      });
    });

    jsonExport(
      flat,
      { rename: Array.from(customHeaderMap.keys()) },
      (err, csv) => {
        if (err) throw err;
        downloadCSV(csv, `products_${new Date().toLocaleDateString()}`);
      },
    );
  } catch (err) {
    logError("products:exportCsv", err, () => {});
  }
}
